/**
 * GovTemplates — Biblioteca de Modelos Prontos.
 *
 * Sprint 2 · v0.98 · Fev/2026.
 * Lista os modelos institucionais (IPTU, ISS, Saúde, CRAS, Educação, Obras,
 * Iluminação, Coleta, Eventos etc.) e permite importar em batch para a Base.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SECRETARIA_LABEL = {
  administracao: "Administração",
  saude: "Saúde",
  educacao: "Educação",
  obras: "Obras",
  assistencia_social: "Assistência Social",
  fazenda: "Fazenda",
  meio_ambiente: "Meio Ambiente",
  procuradoria: "Procuradoria",
  recursos_humanos: "RH",
  gabinete: "Gabinete",
  cultura_esporte: "Cultura/Esporte",
  seguranca: "Segurança",
  ouvidoria: "Ouvidoria",
};

export default function GovTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/gov/knowledge/templates");
        setTemplates(data?.templates || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar modelos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (code) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(code)) nx.delete(code); else nx.add(code);
      return nx;
    });
  };
  const toggleAll = () => {
    if (selected.size === templates.length) setSelected(new Set());
    else setSelected(new Set(templates.map((t) => t.code)));
  };

  const doImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/gov/knowledge/templates/import", {
        codes: Array.from(selected),
      });
      toast.success(
        `${data.created?.length || 0} modelos importados para a Base Institucional.`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="gov-templates-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">
          Biblioteca oficial
        </div>
        <h1 className="font-heading text-[26px] font-semibold text-slate-900 mt-1">
          Modelos Prontos
        </h1>
        <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">
          Conteúdos genéricos preparados por especialistas em Gestão Pública.
          Importe os que fazem sentido e edite conforme a realidade do município.
        </p>
      </header>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={toggleAll}
          data-testid="gov-templates-toggle-all"
          className="text-[12px] text-emerald-700 hover:underline"
        >
          {selected.size === templates.length && templates.length > 0
            ? "Desmarcar todos"
            : "Selecionar todos"}
        </button>
        <button
          type="button"
          onClick={doImport}
          disabled={selected.size === 0 || importing}
          data-testid="gov-templates-import-btn"
          className="px-4 py-2 rounded-md bg-emerald-700 text-white text-sm hover:bg-emerald-800 disabled:opacity-50"
        >
          {importing ? "Importando…" : `Importar ${selected.size || ""} selecionado(s)`}
        </button>
      </div>

      {loading ? (
        <div className="text-[12.5px] text-slate-500">Carregando modelos…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="gov-templates-grid">
          {templates.map((t) => {
            const isSel = selected.has(t.code);
            return (
              <button
                key={t.code}
                type="button"
                onClick={() => toggle(t.code)}
                data-testid={`gov-template-${t.code}`}
                className={`text-left rounded-xl p-4 border transition-colors ${
                  isSel
                    ? "border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-200"
                    : "border-slate-200 bg-white hover:border-emerald-300"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-slate-900 text-[13.5px] leading-tight">
                    {t.title}
                  </div>
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center text-[11px] ${
                      isSel
                        ? "bg-emerald-700 text-white"
                        : "border border-slate-300"
                    }`}
                  >
                    {isSel ? "✓" : ""}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-[10.5px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">
                    {SECRETARIA_LABEL[t.secretaria] || t.secretaria}
                  </span>
                  <span className="text-[10.5px] text-slate-400">
                    {t.chars.toLocaleString("pt-BR")} caracteres
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
